import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CaseItem } from '@shared/models/case-item.model';
import {lastValueFrom, Observable, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import { TenantService, NotificationMessageService } from '@root/app/shared/services';
import { LoaderProxyService } from '@root/app/shared/services/loader-proxy.service';
import {TranslateService} from '@ngx-translate/core';
import {MessageType} from '@shared/constants/message-type.constant';
import { PDFDocument } from 'pdf-lib';
import {ServiceStatus} from '@modules/cases/models/Statuses';
import {Service} from '@modules/cases/models/Services';
import {ServiceMessage} from '@modules/cases/models/ServiceMessage';
import {CaseItemIdSet} from '@modules/cases/models/CaseItemIdSet';

export class CustomHttpParams extends HttpParams {
  constructor(public dontCatchErrors: boolean) {
   super();
  }
}


@Injectable({
  providedIn: 'root'
})
export class CaseItemService {

  private acceptedImageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
  private caseItemUpdate: Subject<CaseItem> = new Subject<CaseItem>();
  public caseItemUpdate$ = this.caseItemUpdate.asObservable();
  private baseUrlApi;
  private basePathApi;

  constructor(private http: HttpClient, private notificationMessageService: NotificationMessageService,
              private tenantService: TenantService, private loaderProxy: LoaderProxyService, private translate: TranslateService) {
    this.baseUrlApi = this.tenantService.getParameter('sorrento', 'baseUrlApi');
    this.basePathApi = this.tenantService.getParameter('sorrento', 'basePathApi');
  }


  async getServiceStatusFromS3(service: Service, page: number, serviceStatusUrls): Promise<ServiceStatus>{
    const fileName = service + page;
    let result = null;

    const serviceMessageUrl = serviceStatusUrls?.find(item => item.pageNr === fileName);
    if (serviceMessageUrl && serviceMessageUrl.preSignedUrl) {
      await lastValueFrom(this.http.get(serviceMessageUrl.preSignedUrl, {params: new CustomHttpParams(true)})).then((data: ServiceMessage) => {
        result = data.serviceStatus;
      });
    }
    return result;
  }


  public checkFileAllowed(file: any){
    // Images can be max 20MB
    if (file.size > 20 * 1024 * 1024 && file.type !== 'application/pdf'){
      this.notificationMessageService.openBarMessage(this.translate.instant('Error.FileTooLarge'), MessageType.ERROR);
      return false;
    }
    // File should be image or pdf
    if (!this.acceptedImageTypes.includes(file.type)){
      this.notificationMessageService.openBarMessage(this.translate.instant('Error.NotImageFormat'), MessageType.ERROR);
      return false;
    }
    return true;
  }


  // get case item based on caseitemid
  // To create a new case item, send following request with the classId as the caseItemId
  public async getCaseItem(caseId: string, userId: string, caseItemId: string): Promise<CaseItem> {
    const reqUrl = `${this.baseUrlApi}/${this.basePathApi}/cases/${caseId}/users/${userId}/caseitems/${caseItemId}`;
    const headers = {};
    const caseItemObs = this.http.get<any>(reqUrl, headers).pipe(
      map((caseItem: any) =>
        this.toCaseItem(caseItem)
      ));
    return lastValueFrom(caseItemObs);
  }

  public async getCaseItemByIdSet(caseIds: CaseItemIdSet): Promise<CaseItem> {
    return this.getCaseItem(caseIds.caseId, caseIds.userId, caseIds.caseItemId);
  }

  // make new case item based on 'data'
  public toCaseItem(data: any): CaseItem {
    return new CaseItem(
      data.masterId,
      data.entryId,
      data.creationTime,
      data.updateTime,
      data.currentVersion,
      data.class,
      data.name,
      data.typeLocked,
      data.type,
      data.presignedUrls,
      data.s3Objects,
      data.serviceStatuses,
      data.retriesPerPage,
      data.status,
      data.recognisedData,
      data.confirmedData,
      data.review,
      data.maxRetries,
      data.main
    );
  }

  // get file Blob from S3 with presignedUrl, used to display image
  public async getFileWithPreSignedUrl(preSignedUrl: string) {
    return lastValueFrom(this.http.get(preSignedUrl, { responseType: 'blob', })).then(data => {
      return data;
    });
  }

  public async getFileContentsWithPreSignedUrl(preSignedUrl: string) {
    return lastValueFrom(this.http.get(preSignedUrl, {params: new CustomHttpParams(true)})).then(data => {
      return data;
    });
  }


  // returns a presignedUrl for every pageNr in a caseItem
  public async getPreSignedUrl(caseId: string, userId: string, caseItemId: string, pageNr: any) {
    const reqUrl = `${this.baseUrlApi}/${this.basePathApi}/cases/${caseId}/users/${userId}/caseitems/${caseItemId}/${pageNr}`;
    return lastValueFrom(this.http.get(reqUrl)).then(data => {
      return data;
    });
  }


  public async uploadFile(file: File, caseItemIds: CaseItemIdSet, originPage: number, caseItemStatus) {
    // Images can only be uploaded with status InProgress => If status is uploaded, set it back to InProgress
    const caseId  = caseItemIds.caseId;
    const userId = caseItemIds.userId;
    const caseItemId = caseItemIds.caseItemId;

    if (caseItemStatus !== 'InProgress' && caseItemStatus !== 'Created') {
      await this.updateCaseItem({ status: 'InProgress' }, caseItemIds);
    }

    // Upload file to s3
    const fileContents = await this.readFileAsArrayBuffer(file);
    const urlData = await this.getPreSignedUrl(caseId, userId, caseItemId, originPage) as any;
    await this.saveFileInS3(urlData.preSignedUrl, fileContents, file.type);

    if (file.type === 'application/pdf') {
      this.checkFileSize(file);
    }

    // trigger convert and process (and services)
    const reqUrl = `${this.baseUrlApi}/${this.basePathApi}/cases/${caseId}/users/${userId}/caseitems/${caseItemId}/${originPage}/convertAndProcess`;

    console.log('Posting to convert and process');
    const req = this.http.post(reqUrl, null).pipe(
      catchError((err) => {
        console.log('Error while posting to convertAndProcess: ', err);
        return this.deleteFile(caseId, userId, caseItemId, originPage);
      })
    );

    return lastValueFrom(req);
  }


  // Display a long processing time warning if a large file has been uploaded
  public async checkFileSize(file){
    const reader = new FileReader();

    reader.onload = async () => {
      const pdfPromise = PDFDocument.load(reader.result, {ignoreEncryption: true});

      pdfPromise.then(
        (pdf) => {
          const count = pdf.getPageCount();
          if (count > 10) {
            this.notificationMessageService.openBarMessage(this.translate.instant('Warning.BigFile'), MessageType.INFO);
          }
        },
        (error) => {
          console.log('Ignoring error loading pdf document while checking size: ' + error);
        }
      );
    };

    reader.onerror = error => {
      console.log('Ignoring error reading file while checking size: ' + error.target.error.name);
      console.log(error.target.error.message);
    };

    reader.readAsArrayBuffer(file);
  }

  public async deleteFile(caseId: string, userId: string, caseItemId: string, pageNr: any) {
    console.log('Going to delete page');
    const reqUrl = `${this.baseUrlApi}/${this.basePathApi}/cases/${caseId}/users/${userId}/caseitems/${caseItemId}/${pageNr}`;
    return lastValueFrom(this.http.delete(reqUrl)).then(data => {
      return data;
    });
  }

  public async saveFileInS3(preSignedUrl: string, fileContents: any, contentType: string) {
    await lastValueFrom(this.http.put(preSignedUrl, fileContents, { headers: new HttpHeaders({ 'Content-Type': contentType }) })
      ).then(() => {
        console.log('File has been put in s3');
      });
  }

  public async updateCaseItem(valueModified: any, caseIds: CaseItemIdSet) {
    const reqUrl = `${this.baseUrlApi}/${this.basePathApi}/cases/${caseIds.caseId}/users/${caseIds.userId}/caseitems/${caseIds.caseItemId}`;
    return lastValueFrom(this.http.patch(reqUrl, valueModified)).then((data: any) => {
      const caseItem = this.toCaseItem(data);
      this.caseItemUpdate.next(caseItem);
      return caseItem;
    });
  }

  // used for when doctype changes, happens in the onSubmit of the class-type.component
  public async updateTypeOfCaseItemWithSelection(typeIdSelection: string, caseIds: CaseItemIdSet) {
    const typeModified = {type: typeIdSelection};
    return await this.updateCaseItem(typeModified, caseIds);
  }


  public async readFileAsArrayBuffer(file: File): Promise<any> {
    const temporaryFileReader = new FileReader();
    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort();
        reject(new DOMException('Problem parsing input file.'));
      };
      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result);
      };
      temporaryFileReader.readAsArrayBuffer(file);
    });
  }

  public async readFileAsDataURL(file: File) {
    const temporaryFileReader = new FileReader();
    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort();
        reject(new DOMException('Problem parsing input file.'));
      };
      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result);
      };
      temporaryFileReader.readAsDataURL(file);
    });
  }
}
